import { type Observable, Subject as RXSubject, distinctUntilChanged, filter, take } from "rxjs";
import { onMounted, onUnmounted, readonly, ref } from "vue";

export type SubjectSubscribeOptions<T> = {
  condition?: (data: T) => boolean;
  onlyChanges?: boolean;
  once?: boolean;
};
export class Subject<T> {
  private observable: RXSubject<T>;

  constructor() {
    this.observable = new RXSubject<T>();
  }

  next(value: T) {
    this.observable.next(value);
  }

  subscribe(observerOrNext: (value: T) => void, options?: SubjectSubscribeOptions<T>) {
    let stream: RXSubject<T> | Observable<T> = this.observable;
    if (options?.condition) {
      stream = stream.pipe(filter(options.condition));
    }
    if (options?.onlyChanges) {
      stream = stream.pipe(distinctUntilChanged());
    }
    if (options?.once) {
      stream = stream.pipe(take(1));
    }
    const subscribe = stream.subscribe(observerOrNext);
    return () => {
      if (!subscribe.closed) {
        subscribe.unsubscribe();
      }
    };
  }

  useSubscribe(observerOrNext: (value: T) => void, options?: SubjectSubscribeOptions<T>) {
    let unsubscribe: (() => void) | undefined;

    onMounted(() => {
      unsubscribe = this.subscribe(observerOrNext, options);
    });

    onUnmounted(() => {
      unsubscribe?.();
    });
  }

  useObserve(options?: SubjectSubscribeOptions<T>): GenericReadonlyRef<T | undefined> {
    const observableValue = ref<T | undefined>(undefined);

    this.useSubscribe((value) => {
      observableValue.value = value;
    }, options);

    return readonly(observableValue);
  }
}
